/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCustomNavigate } from '../../../../../shared/hooks/use-conversation-navigate';
import useQuery from '../../../../../shared/hooks/useQuery';
import usePaginatedQuery from '../../../../../shared/hooks/usePaginatedQuery';
import { getCurrentUserId } from '../../../../../infra/firebase/init';
import { CHAT_FILTER_TYPES, chatFilterAccordionStyles } from '../../constants';
import { ALL_CHANNELS, DEFAULT_MEMBER, DEFAULT_TEAM } from '../../../../../shared/constants';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ButtonBase,
  Tooltip,
  debounce,
  Link,
} from '@mui/material';
import DropdownVirtualList from '../../../../../shared/modules/dropdown/DropdownVirtualList';
import { QSTypography } from '../../../../../shared/components/atoms';
import { QueryRequest } from '../../../../../shared/typings/request';
import {
  FiltersState,
  chatFilterStatusCountsAtom,
  chatFiltersSetter,
  chatListAtom,
  customFieldConditionsAtom,
  isCustomFieldUpdatedAtom,
  showFiltersAtom,
} from '../../states/chat-list';
import { chatIdsAtom, realTimeChatCountStatusAtom, updateChatCountAtom } from '../../states/chat';
import { WabaChannelSort, WabaIntegration, WabaIntegrationPaginationParams } from '../../../../../shared/typings/waba';
import {
  ChatFilterSlaStatus,
  ChatListAtom,
  ChatListReducerActionTypes,
  ChatStatusOptions,
  ChatStatuses,
  ConversationTag,
  GetChatRealtimeCountStatusResponse,
  GetConversationTagsPaginationData,
  TeamMember,
} from '../../typings';
import {
  paginatedFiltersConversationTagsAtom,
  paginatedFiltersConversationTagsSearchAtom,
} from '../../states/conversation-tags';
import { getConversationTagsV2 } from '../../requests/get-conversation-tags';
import { teamMembersAtom } from '../../states/team-members';
import { getTeamMembers } from '../../requests/get-team-members';
import { getWabaIntegrations } from '../../../../../shared/requests/waba';
import {
  wabaIntegrationsAtomFamily,
  wabaIntegrationsOriginalAtomFamily,
  wabaIntegrationsSearchAtomFamily,
} from '../../../../../shared/states/waba';
import { getChatRealtimeCountStatus, getChatStatusFilterCountsLatest, getChatCounts } from '../../requests/get-chat';
import { useInternalOrgs } from '../../hooks/use-internal-orgs';
import { GetChatStatusFilterCount } from '../chat-list-section/chat-row/typings';
import { getOrganizationsSocket } from '../../../../../infra/sockets/organization-socket';
import useSocket from '../../../../../shared/hooks/use-socket';
import { getMessageStatusesSocket, getNewMessageSocket } from '../../../../../infra/sockets';
import { selectedChatIdAtom } from '../../states/selected-chat';
import useDebounce from '../../../../../shared/hooks/use-debounce';
import usePrevious from '../../../../../shared/hooks/usePrevious';
import { sortTagsBasedOnSelection } from '../../utils/sort-tags';
import { purchaseBlockerModalAtom } from '../../../../../shared/components/molecules/purchase-blocker-modal/states';
import { PurchaseBlockerModalType } from '../../../../../shared/components/molecules/purchase-blocker-modal/typings';
import QSTooltip from '../../../../../shared/components/molecules/tooltip';
import { deserializeTagIdsFromQueryParams, deserializeTagsFromQueryParams } from '../../utils/deserialize-tags';
import { isEmbedAtom } from '../../../../embed/state';
import useUpdateFilterCounts from './hooks/use-update-filter-count';
import { useSubscribedPlan } from '../../../../renew-plan/v1/hooks/use-subscribed-plan';
import { PlanAddonTypes } from '../../../../renew-plan/v1/typings';
import { shouldHideDTDetails } from '../../../../login/v1/utility';
import Loader from '../../../../../shared/components/molecules/loader';
import ErrorInAPIComp from './ErrorInAPIComp';
import RefreshChatCountIcon from './refresh-chat-count-icon';
import ArrowExpandIcon from '../../../../../shared/icons/icons-revamp/arrow_expand';
import MenuToggleIcon from '../../../../../shared/icons/icons-revamp/menu_toggle';
import HelpIcon from '../../../../../shared/icons/icons-revamp/help';
import ChatAllIcon from '../../../../../shared/icons/icons-revamp/chat_all';
import MyChatsIcon from '../../../../../shared/icons/icons-revamp/chat_agent';
import UnassignedChatsIcon from '../../../../../shared/icons/icons-revamp/agent_unassign';
import SLABreachedIcon from '../../../../../shared/icons/icons-revamp/sla_time';
import BroadcastGroupIcon from '../../../../../shared/icons/icons-revamp/user_group';
import { isFilterAIAwaitingResponseEnabled } from '../chat-list-section/ai-filtering/state';
import AiAwaitingReplyImg from '../../../../../shared/assets/img/ai-awaiting-reply.png';
import AiAwaitingReplyDisabledImg from '../../../../../shared/assets/img/ai-awaiting-reply-disabled.png';
import { socketEventReceivedAtom } from '../../states/message';
import { buildPlaceholderChatFilterCounts, normalizeFilterCountsForUi } from './utils/build-placeholder-filter-counts';
import WabaIntegrationIcon from '../../../../../shared/components/molecules/waba-integration-logo';
import { conditionsSignature, filterCompleteConditions } from '../chat-list-section/custom-field-filter/utils';
import { CustomFieldFilterPopovers, CustomFieldFilterSidebarEntry } from '../chat-list-section/custom-field-filter';
import { useCustomFieldConditionsUrlSync } from '../chat-list-section/custom-field-filter/hooks/use-cfc-url-sync';

const ChatFilters = () => {
  const location = useLocation();
  const routerNavigate = useNavigate();
  const navigate = useCustomNavigate();
  const currentUserId = getCurrentUserId();
  const { isAddonPresent } = useSubscribedPlan(false);
  const setPurchaseBlockerModal = useSetAtom(purchaseBlockerModalAtom);
  const { socket: organisationSocket } = useSocket(getOrganizationsSocket);
  const { socket: conversationSocket } = useSocket(getNewMessageSocket, undefined, undefined, undefined, undefined, {
    doNotDisconnectOnUnmount: true,
    reconnectOnFocus: false,
    reconnectOnVisibility: false,
  });
  const chatId = useAtomValue(selectedChatIdAtom);
  const prevLeadCustomFieldFeildId = useRef<string | undefined>(undefined);
  const [isTabVisible, setIsTabVisible] = useState(true);
  const { isInternal } = useInternalOrgs();

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsTabVisible(!document.hidden);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const messageStatusSocket = useMemo(() => {
    if (chatId) return getMessageStatusesSocket(chatId);
  }, [chatId]);

  const {
    tags: selectedTagFilters,
    status: selectedChatStatus,
    assignedUserId,
    waba: selectedWabaFilters,
    slaStatus: selectedSlaStatus,
    searchTerm,
    leadCustomFieldIdList,
    tagIds,
    customList,
    conversationView,
    customFieldConditions,
  } = useAtomValue(chatListAtom);
  const customFieldConditionsSignature = useMemo(
    () => conditionsSignature(customFieldConditions ?? []),
    [customFieldConditions]
  );
  const hasCustomFieldConditions = useMemo(
    () => filterCompleteConditions(customFieldConditions ?? []).length > 0,
    [customFieldConditions]
  );

  const sendRealTimeChatFiltersData = useCallback(
    (data: Partial<ChatListAtom>) => {
      const eventName = 'chat-filters-update';
      const payload: Record<string, string | string[] | undefined | null | {}> = {
        tags: selectedTagFilters,
        status: selectedChatStatus,
        slaStatus: selectedSlaStatus,
        waba: selectedWabaFilters,
        searchTerm,
        ...data,
        assignedUserId:
          data?.assignedUserId && data?.assignedUserId !== 'null'
            ? data?.assignedUserId
            : data?.assignedUserId === 'null'
            ? null
            : undefined,
      };
      if (leadCustomFieldIdList?.fieldId) {
        payload.customFields_v2 = [
          {
            id: leadCustomFieldIdList.listId,
            value: leadCustomFieldIdList.fieldId,
          },
        ];
      }

      organisationSocket.emit(eventName, payload);
      conversationSocket.emit(eventName, payload);
      if (messageStatusSocket) {
        messageStatusSocket.emit(eventName, payload);
      }
    },
    [
      selectedTagFilters,
      selectedChatStatus,
      selectedSlaStatus,
      assignedUserId,
      selectedWabaFilters,
      searchTerm,
      organisationSocket,
      conversationSocket,
      messageStatusSocket,
      leadCustomFieldIdList,
    ]
  );

  const isEmbed = useAtomValue(isEmbedAtom);

  const [showChatFilters, setShowChatFilters] = useAtom(showFiltersAtom);
  const [expandedChatFilterTypes, setExpandedChatFilterTypes] = useState<FiltersState[]>(new Array<FiltersState>());
  const [tagFilterDropdownOpen, setTagFilterDropdownOpen] = useState(false);
  const [assigneeFilterDropdownOpen, setAssigneeFilterDropdownOpen] = useState(false);
  const [helpIconHovered, setHelpIconHovered] = useState(false);
  const chatStatusFilterCountsData = useAtomValue(chatFilterStatusCountsAtom);
  const hideDTDetails = shouldHideDTDetails();
  const [updateChatCount, setUpdateChatCount] = useAtom(updateChatCountAtom);

  const [conversationTagsSearchTerm, setConversationTagsSearchTerm] = useState<string>('');
  const debouncedConversationTagsSearchTerm = useDebounce<string>(conversationTagsSearchTerm ?? '', 300);
  const lastFetchedTagsSearchTermRef = useRef<string>('');
  const refreshConversationTagsSearchRef = useRef<(() => Promise<void>) | null>(null);

  const [conversationStatusSearchTerm, setConversationStatusSearchTerm] = useState<string>('');
  const debouncedSetConversationStatusSearchTerm = debounce((searchTerm: string) => {
    setConversationStatusSearchTerm?.(searchTerm);
  }, 500);
  const [haveLoadedFilters, setHaveLoadedFilters] = useState(false);

  const [conversationAssigneeSearchTerm, setConversationAssigneeSearchTerm] = useState<string>('');
  const debouncedSetConversationAssigneeSearchTerm = debounce((searchTerm: string) => {
    setConversationAssigneeSearchTerm?.(searchTerm);
  }, 500);

  const [wabaIntegrationSearchTerm, setWabaIntegrationSearchTerm] = useState<string>('');
  const debouncedWabaIntegrationSearchTerm = useDebounce<string>(wabaIntegrationSearchTerm ?? '', 500);
  const lastFetchedWabaSearchTermRef = useRef<string>('');
  const refreshWabaIntegrationsSearchRef = useRef<(() => Promise<void>) | null>(null);

  const setChatFilters = useSetAtom(chatFiltersSetter);
  const setCustomFieldConditions = useSetAtom(customFieldConditionsAtom);

  const { loading: paginatedChatsLoading } = useAtomValue(chatIdsAtom);
  const previousFilterCountsRef = useRef<GetChatStatusFilterCount[]>([]);
  const previousFilterCountsErrorRef = useRef<Error | undefined>(undefined);
  /** True after the filter-counts API succeeds at least once; used for initial-load-only retry UI and keeping badges visible during refetch. */
  const filterCountsApiSucceededOnceRef = useRef(false);

  const realTimeChatCountStatusGetter = useCallback(async (): Promise<GetChatRealtimeCountStatusResponse> => {
    const response = await getChatRealtimeCountStatus();
    return {
      ...response,
      status: response.liveChatCountsResolution !== null,
    };
  }, []);

  const {
    requestData: { data: realTimeChatCountStatus },
  } = useQuery<GetChatRealtimeCountStatusResponse>({
    requestAtom: realTimeChatCountStatusAtom,
    queryFunction: realTimeChatCountStatusGetter,
  });

  const socketEventReceived = useAtomValue(socketEventReceivedAtom);
  const filterAIAwaitingResponse = useAtomValue(isFilterAIAwaitingResponseEnabled);
  const prevassignedUserId = usePrevious(assignedUserId);
  const prevleadCustomFieldIdList = usePrevious(leadCustomFieldIdList);
  const prevselectedTagFilters = usePrevious(selectedTagFilters);
  const prevselectedWabaFilters = usePrevious(selectedWabaFilters);
  const prevcustomList = usePrevious(customList);
  const prevrealTimeChatCountStatus = usePrevious(realTimeChatCountStatus);
  const prevupdateChatCount = usePrevious(updateChatCount);
  const prevsearchTerm = usePrevious(searchTerm);
  const prevfilterAIAwaitingResponse = usePrevious(filterAIAwaitingResponse);
  const prevconversationView = usePrevious(conversationView);
  const prevCustomFieldConditionsSignature = usePrevious(customFieldConditionsSignature);
  const bypassCacheRef = useRef(false);
  const normalizeFilterArray = (arr?: string[]) => (arr ?? []).filter(Boolean).slice().sort().join(',');
  const areFilterArraysEqual = (prev?: string[], curr?: string[]) =>
    normalizeFilterArray(prev) === normalizeFilterArray(curr);

  const chatStatusFilterCountsGetter = useCallback(async (): Promise<GetChatStatusFilterCount[]> => {
    if (
      !bypassCacheRef.current &&
      prevassignedUserId === assignedUserId &&
      prevleadCustomFieldIdList === leadCustomFieldIdList &&
      areFilterArraysEqual(prevselectedTagFilters, selectedTagFilters) &&
      areFilterArraysEqual(prevselectedWabaFilters, selectedWabaFilters) &&
      prevcustomList === customList &&
      prevrealTimeChatCountStatus === realTimeChatCountStatus &&
      prevupdateChatCount === updateChatCount &&
      prevsearchTerm === searchTerm &&
      prevfilterAIAwaitingResponse === filterAIAwaitingResponse &&
      prevconversationView === conversationView &&
      prevCustomFieldConditionsSignature === customFieldConditionsSignature
    ) {
      return previousFilterCountsRef.current ?? [];
    }
    bypassCacheRef.current = false;
    try {
      let mergedCustomList = customList;
      if (leadCustomFieldIdList?.listId) {
        mergedCustomList = {
          ...mergedCustomList,
          ...{ [leadCustomFieldIdList.listId]: leadCustomFieldIdList?.fieldId },
        };
      }

      // Check if waba, agent (assignedUserId), or tags filters have changed
      const hasWabaFilterChanged = !areFilterArraysEqual(prevselectedWabaFilters, selectedWabaFilters);
      const hasAgentFilterChanged = prevassignedUserId !== assignedUserId;
      const hasTagsFilterChanged = !areFilterArraysEqual(prevselectedTagFilters, selectedTagFilters);

      const shouldUseNewAPI =
        isInternal &&
        !hasCustomFieldConditions &&
        (hasWabaFilterChanged || hasAgentFilterChanged || hasTagsFilterChanged);
      const chatType = conversationView === 'groups' ? 'WA_GROUP' : undefined;

      let filterCounts: GetChatStatusFilterCount[];
      if (shouldUseNewAPI) {
        const response = await getChatCounts({
          assignedUserId,
          customList: mergedCustomList,
          tags: selectedTagFilters,
          waba: selectedWabaFilters,
          tagIds,
          searchTerm,
          chatType,
        });
        filterCounts = response.filterCounts ?? [];
      } else {
        const response = await getChatStatusFilterCountsLatest({
          assignedUserId,
          customList: mergedCustomList,
          tags: selectedTagFilters,
          waba: selectedWabaFilters,
          searchTerm,
          filterAIAwaitingResponse,
          chatType,
          customFieldConditions,
        });
        filterCounts = response.filterCounts ?? [];
      }

      setHaveLoadedFilters(true);
      const newData = normalizeFilterCountsForUi(filterCounts ?? []);
      previousFilterCountsRef.current = newData;
      previousFilterCountsErrorRef.current = undefined;
      filterCountsApiSucceededOnceRef.current = true;
      return newData;
    } catch (error) {
      previousFilterCountsErrorRef.current = error as Error;
      return previousFilterCountsRef.current ?? [];
    }
  }, [
    assignedUserId,
    leadCustomFieldIdList,
    selectedTagFilters,
    selectedWabaFilters,
    customList,
    realTimeChatCountStatus,
    updateChatCount,
    searchTerm,
    filterAIAwaitingResponse,
    prevfilterAIAwaitingResponse,
    prevconversationView,
    tagIds,
    conversationView,
    customFieldConditions,
    customFieldConditionsSignature,
    prevCustomFieldConditionsSignature,
    hasCustomFieldConditions,
  ]);

  const {
    requestData: {
      data: chatStatusFilterCounts,
      loading: chatStatusFilterCountsLoading,
      refreshing: chatStatusFilterCountsRefreshing,
      error: chatStatusFilterCountsError,
    },
    fetchData: fetchChatStatusFilterCountsData,
    setData: setChatStatusFilterCountsData,
  } = useQuery<GetChatStatusFilterCount[]>({
    requestAtom: chatFilterStatusCountsAtom,
    queryFunction: chatStatusFilterCountsGetter,
  });

  const shouldSkipPlaceholderFilterInjection = useMemo(
    () => isEmbed && new URLSearchParams(location.search).get('showChatFilters') === 'false',
    [isEmbed, location.search]
  );

  useLayoutEffect(() => {
    if (shouldSkipPlaceholderFilterInjection) {
      return;
    }
    // After real counts have loaded once, do not replace atom with placeholder on conversationView / AI
    // toggle — zeros would hide badges until the next request completes (e.g. All chats → Groups).
    if (filterCountsApiSucceededOnceRef.current) {
      return;
    }
    const placeholder = normalizeFilterCountsForUi(buildPlaceholderChatFilterCounts());
    setChatStatusFilterCountsData(placeholder);
    previousFilterCountsRef.current = placeholder;
    setHaveLoadedFilters(true);
    // setChatStatusFilterCountsData omitted from deps: useQuery setData identity changes with requestData; including it caused an infinite loop after useQuery reverted to [requestData, setRequestData] deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setChatStatusFilterCountsData intentionally unstable from useQuery
  }, [conversationView, filterAIAwaitingResponse, shouldSkipPlaceholderFilterInjection]);

  const refreshFilterCounts = useCallback(() => {
    bypassCacheRef.current = true;
    if (isTabVisible) {
      fetchChatStatusFilterCountsData(true);
    }
  }, [fetchChatStatusFilterCountsData, isTabVisible]);
  useUpdateFilterCounts({
    refreshFilterCounts,
    realTimeCountStatus: realTimeChatCountStatus?.status ?? false,
    liveChatCountsRefreshInterval: realTimeChatCountStatus?.liveChatCountsRefreshInterval ?? 5 * 1000,
    searchTerm,
    isTabVisible,
  });

  const isCustomFieldUpdated = useAtomValue(isCustomFieldUpdatedAtom);
  useEffect(() => {
    if (isCustomFieldUpdated && hasCustomFieldConditions) {
      refreshFilterCounts();
    }
  }, [isCustomFieldUpdated, hasCustomFieldConditions, refreshFilterCounts]);

  useCustomFieldConditionsUrlSync();

  // Getter for initial tags list (without search)
  const conversationTagsInitialGetter = useCallback(
    async (paginationData?: GetConversationTagsPaginationData) => {
      const {
        tags: tagsData,
        hasMore,
        nextSearchAfter,
      } = await getConversationTagsV2({
        searchAfter: paginationData?.nextSearchAfter,
        searchTerm: '',
        selectedTags: selectedTagFilters,
      });

      return {
        data: tagsData,
        paginationData: {
          hasMore,
          nextSearchAfter,
        },
      };
    },
    [selectedTagFilters]
  );

  // Getter for tags search results
  const conversationTagsSearchGetter = useCallback(
    async (paginationData?: GetConversationTagsPaginationData) => {
      const {
        tags: tagsData,
        hasMore,
        nextSearchAfter,
      } = await getConversationTagsV2({
        searchAfter: paginationData?.nextSearchAfter,
        searchTerm: debouncedConversationTagsSearchTerm,
        selectedTags: [],
      });

      return {
        data: tagsData,
        paginationData: {
          hasMore,
          nextSearchAfter,
        },
      };
    },
    [debouncedConversationTagsSearchTerm]
  );

  // Query for initial tags list
  const {
    fetchMore: conversationTagsFetchMore,
    requestData: conversationTagsRequestData,
    refresh: refreshConversationTags,
  } = usePaginatedQuery<ConversationTag, GetConversationTagsPaginationData>({
    requestAtom: paginatedFiltersConversationTagsAtom,
    getter: conversationTagsInitialGetter,
    autoFetch: true,
  });

  // Query for tags search results
  const {
    fetchMore: conversationTagsSearchFetchMore,
    requestData: conversationTagsSearchRequestData,
    refresh: refreshConversationTagsSearch,
  } = usePaginatedQuery<ConversationTag, GetConversationTagsPaginationData>({
    requestAtom: paginatedFiltersConversationTagsSearchAtom,
    getter: conversationTagsSearchGetter,
    autoFetch: false,
  });

  // Store refresh function in ref to avoid dependency issues
  refreshConversationTagsSearchRef.current = refreshConversationTagsSearch;

  const chatStatusesRequestData: QueryRequest<{ id: ChatStatuses; name: string }[]> = useMemo(() => {
    return {
      loading: false,
      refreshing: false,
      data: ChatStatusOptions.map(({ value, name }) => ({ id: value, name })),
    };
  }, []);

  const getChatStatusFilterCount = (
    type: keyof Pick<typeof FiltersState, 'ALL_CHATS' | 'MY_CHATS' | 'UNASSIGNED' | 'SLA_STATUS'>,
    status: ChatStatuses
  ) => {
    const chatStatusFilter = chatStatusFilterCounts?.find?.(
      (chatStatusFilterCount) => chatStatusFilterCount.type === type
    );

    const chatStatusFilterCount = chatStatusFilter?.counts?.find?.(
      (chatStatusFilterCount) => chatStatusFilterCount.type === status
    );
    return status === ChatStatuses.ALL ? undefined : chatStatusFilterCount?.count ?? '0';
  };

  const isFilterActiveInAccordion = useCallback(
    (type: FiltersState) => {
      if (type === FiltersState.ALL_CHATS) {
        return assignedUserId !== currentUserId && assignedUserId !== 'null' && selectedSlaStatus !== 'breached';
      } else if (type === FiltersState.MY_CHATS) {
        return (
          typeof assignedUserId === 'string' &&
          assignedUserId.length > 0 &&
          assignedUserId === currentUserId &&
          selectedSlaStatus !== 'breached'
        );
      } else if (type === FiltersState.SLA_STATUS) {
        return selectedSlaStatus === 'breached';
      } else if (type === FiltersState.UNASSIGNED) {
        return assignedUserId === 'null' && selectedSlaStatus !== 'breached';
      }
      return false;
    },
    [assignedUserId, currentUserId, selectedSlaStatus]
  );

  const { requestData: conversationAssigneesRequestData } = useQuery<TeamMember[]>({
    requestAtom: teamMembersAtom,
    queryFunction: getTeamMembers,
    getCachedData: true,
  });

  // Custom handler for team member selection that allows deselection
  const handleTeamMemberChange = useCallback(
    (item: TeamMember) => {
      const currentAssignedUserId = assignedUserId && assignedUserId !== 'null' ? assignedUserId : '';
      const queryParams = new URLSearchParams(location.search);

      // If clicking on the already selected option, clear the selection
      if (item.id === currentAssignedUserId) {
        queryParams.delete('assignedUserId');
      } else {
        queryParams.set('assignedUserId', item.id);
      }
      const newSearch = queryParams.toString();
      // Use routerNavigate directly to ensure URL updates immediately
      routerNavigate(
        {
          pathname: location.pathname,
          search: newSearch ? `?${newSearch}` : '',
        },
        { replace: true }
      );
      // Close dropdown after selection
      setAssigneeFilterDropdownOpen(false);
    },
    [assignedUserId, location.search, navigate]
  );

  // Getter for initial WABA list (without search)
  const wabaIntegrationsInitialGetter = useCallback(async (paginationParams?: WabaIntegrationPaginationParams) => {
    if (!paginationParams) {
      paginationParams = {
        teamId: DEFAULT_TEAM,
        forMemberId: undefined,
        sendOnlyAssignedIntegrations: false,
        excludePrimaryField: true,
        includeAllIntegrations: true,
        sort: WabaChannelSort.NAME_ASC,
        sortAfter: undefined,
        searchTerm: '',
        hasMore: false,
      };
    }
    const data = await getWabaIntegrations(paginationParams, true);
    const { integrations, nextSortAfter, hasMore } = data || {};
    return {
      data: integrations,
      paginationData: {
        ...paginationParams,
        sortAfter: nextSortAfter ?? paginationParams?.nextSortAfter,
        searchTerm: '',
        hasMore: hasMore ?? paginationParams.hasMore,
      } as WabaIntegrationPaginationParams,
    };
  }, []);

  // Getter for WABA search results
  const wabaIntegrationsSearchGetter = useCallback(
    async (paginationParams?: WabaIntegrationPaginationParams) => {
      if (!paginationParams) {
        paginationParams = {
          teamId: DEFAULT_TEAM,
          forMemberId: undefined,
          sendOnlyAssignedIntegrations: false,
          excludePrimaryField: true,
          includeAllIntegrations: true,
          sort: WabaChannelSort.NAME_ASC,
          sortAfter: undefined,
          searchTerm:
            typeof debouncedWabaIntegrationSearchTerm === 'string' &&
            debouncedWabaIntegrationSearchTerm.trim().length > 0
              ? debouncedWabaIntegrationSearchTerm.trim()
              : undefined,
          hasMore: false,
        };
      }
      const data = await getWabaIntegrations(paginationParams, true);
      const { integrations, nextSortAfter, hasMore } = data || {};
      return {
        data: integrations,
        paginationData: {
          ...paginationParams,
          sortAfter: nextSortAfter ?? paginationParams?.nextSortAfter,
          searchTerm:
            typeof debouncedWabaIntegrationSearchTerm === 'string' &&
            debouncedWabaIntegrationSearchTerm.trim().length > 0
              ? debouncedWabaIntegrationSearchTerm.trim()
              : undefined,
          hasMore: hasMore ?? paginationParams.hasMore,
        } as WabaIntegrationPaginationParams,
      };
    },
    [debouncedWabaIntegrationSearchTerm]
  );

  const wabaIntegrationsAtom = useMemo(() => {
    return wabaIntegrationsAtomFamily({ id: ALL_CHANNELS });
  }, []);

  const wabaIntegrationsSearchAtom = useMemo(() => {
    return wabaIntegrationsSearchAtomFamily({ id: ALL_CHANNELS });
  }, []);

  // Query for initial WABA list (fetched when dropdown opens via onDropdownOpen)
  const {
    fetchMore: wabaIntegrationsFetchMore,
    requestData: wabaIntegrationsRequestData,
    refresh: refreshWabaIntegrations,
  } = usePaginatedQuery<WabaIntegration, WabaIntegrationPaginationParams>({
    requestAtom: wabaIntegrationsAtom,
    getter: wabaIntegrationsInitialGetter,
    autoFetch: true,
  });

  // Query for WABA search results
  const {
    fetchMore: wabaIntegrationsSearchFetchMore,
    requestData: wabaIntegrationsSearchRequestData,
    refresh: refreshWabaIntegrationsSearch,
  } = usePaginatedQuery<WabaIntegration, WabaIntegrationPaginationParams>({
    requestAtom: wabaIntegrationsSearchAtom,
    getter: wabaIntegrationsSearchGetter,
    autoFetch: false,
  });

  // Store refresh function in ref to avoid dependency issues
  refreshWabaIntegrationsSearchRef.current = refreshWabaIntegrationsSearch;

  // Get setters for merging search results
  const setWabaIntegrationsData = useSetAtom(wabaIntegrationsAtom);
  const wabaIntegrationsOriginalAtom = useMemo(() => {
    return wabaIntegrationsOriginalAtomFamily({ id: DEFAULT_MEMBER });
  }, []);
  const wabaIntegrationsOriginalData = useAtomValue(wabaIntegrationsOriginalAtom);
  const setWabaIntegrationsOriginalData = useSetAtom(wabaIntegrationsOriginalAtom);

  // Trigger tags search when search term changes
  useEffect(() => {
    const trimmedSearchTerm = debouncedConversationTagsSearchTerm?.trim() ?? '';
    const hasSearchTerm = trimmedSearchTerm.length > 0;
    const searchTermChanged = lastFetchedTagsSearchTermRef.current !== trimmedSearchTerm;
    const isNotLoading = !conversationTagsSearchRequestData?.loading && !conversationTagsSearchRequestData?.refreshing;

    if (hasSearchTerm && searchTermChanged && isNotLoading && refreshConversationTagsSearchRef.current) {
      lastFetchedTagsSearchTermRef.current = trimmedSearchTerm;
      refreshConversationTagsSearchRef.current();
    } else if (!hasSearchTerm) {
      // Reset ref when search term is cleared
      lastFetchedTagsSearchTermRef.current = '';
    }
  }, [
    debouncedConversationTagsSearchTerm,
    conversationTagsSearchRequestData?.loading,
    conversationTagsSearchRequestData?.refreshing,
  ]);

  // Trigger WABA search when search term changes
  useEffect(() => {
    const trimmedSearchTerm = debouncedWabaIntegrationSearchTerm?.trim() ?? '';
    const hasSearchTerm = trimmedSearchTerm.length > 0;
    const searchTermChanged = lastFetchedWabaSearchTermRef.current !== trimmedSearchTerm;
    const isNotLoading = !wabaIntegrationsSearchRequestData?.loading && !wabaIntegrationsSearchRequestData?.refreshing;

    if (hasSearchTerm && searchTermChanged && isNotLoading && refreshWabaIntegrationsSearchRef.current) {
      lastFetchedWabaSearchTermRef.current = trimmedSearchTerm;
      refreshWabaIntegrationsSearchRef.current();
    } else if (!hasSearchTerm) {
      // Reset ref when search term is cleared
      lastFetchedWabaSearchTermRef.current = '';
      if (searchTermChanged) {
        refreshWabaIntegrations();
      }
    }
  }, [
    debouncedWabaIntegrationSearchTerm,
    wabaIntegrationsSearchRequestData?.loading,
    wabaIntegrationsSearchRequestData?.refreshing,
  ]);

  // Merge WABA search results into original data so selected WABAs are available for display
  useEffect(() => {
    const searchResults = wabaIntegrationsSearchRequestData?.data;
    if (!searchResults || searchResults.length === 0) return;

    const originalData = wabaIntegrationsOriginalData?.data ?? [];
    const existingIds = new Set(originalData.map((waba) => waba.id));

    // Find WABAs in search results that aren't in original data
    const newWabas = searchResults.filter((waba) => !existingIds.has(waba.id));

    if (newWabas.length > 0) {
      // Merge new WABAs into original data
      setWabaIntegrationsOriginalData((prev) => ({
        ...prev,
        data: [...(prev.data ?? []), ...newWabas],
      }));
    }
  }, [wabaIntegrationsSearchRequestData?.data, wabaIntegrationsOriginalData?.data, setWabaIntegrationsOriginalData]);

  // Merge WABA search results into main data so selected WABAs are available for display
  useEffect(() => {
    const searchResults = wabaIntegrationsSearchRequestData?.data;
    if (!searchResults || searchResults.length === 0) return;

    const mainData = wabaIntegrationsRequestData?.data ?? [];
    const existingIds = new Set(mainData.map((waba) => waba.id));

    // Find WABAs in search results that aren't in main data
    const newWabas = searchResults.filter((waba) => !existingIds.has(waba.id));

    if (newWabas.length > 0) {
      // Merge new WABAs into main data
      setWabaIntegrationsData((prev) => ({
        ...prev,
        data: [...(prev.data ?? []), ...newWabas],
      }));
    }
  }, [wabaIntegrationsSearchRequestData?.data, wabaIntegrationsRequestData?.data, setWabaIntegrationsData]);

  const chatStatusFilterCountsMap = useMemo(() => {
    return CHAT_FILTER_TYPES.filter(({ type }) => {
      if (type === FiltersState.ASSIGNEE && assignedUserId === 'null') {
        return false;
      }
      if (
        type === FiltersState.TAGS &&
        (!conversationTagsRequestData?.data || conversationTagsRequestData?.data?.length === 0)
      ) {
        return conversationTagsSearchTerm && conversationTagsSearchTerm.length > 0;
      }
      if (
        type === FiltersState.WABA &&
        (!wabaIntegrationsRequestData?.data || wabaIntegrationsRequestData?.data?.length === 0)
      ) {
        return wabaIntegrationSearchTerm && wabaIntegrationSearchTerm?.length > 0;
      }
      if (
        [FiltersState.ALL_CHATS, FiltersState.MY_CHATS, FiltersState.SLA_STATUS, FiltersState.UNASSIGNED].indexOf(
          type
        ) > -1
      ) {
        return (
          (chatStatusFilterCounts ?? [])?.findIndex?.((chatStatusFilterCount) => chatStatusFilterCount.type === type) >
          -1
        );
      }
      return true;
    });
  }, [
    assignedUserId,
    chatStatusFilterCounts,
    wabaIntegrationsRequestData?.data,
    conversationTagsRequestData?.data,
    wabaIntegrationSearchTerm,
  ]);
  useEffect(() => {
    const eventName = 'chat-filters-update';
    const payload = {
      tags: selectedTagFilters,
      status: selectedChatStatus,
      slaStatus: selectedSlaStatus,
      assignedUserId:
        assignedUserId && assignedUserId !== 'null' ? assignedUserId : assignedUserId === 'null' ? null : undefined,
      waba: selectedWabaFilters,
      searchTerm,
    };
    organisationSocket.emit(eventName, payload);
    conversationSocket.emit(eventName, payload);
    if (messageStatusSocket) {
      messageStatusSocket.emit(eventName, payload);
    }
  }, []);

  const onOrganizationSocketConnect = () => {
    const eventName = 'chat-filters-update';
    const payload = {
      tags: selectedTagFilters,
      status: selectedChatStatus,
      slaStatus: selectedSlaStatus,
      assignedUserId:
        assignedUserId && assignedUserId !== 'null' ? assignedUserId : assignedUserId === 'null' ? null : undefined,
      waba: selectedWabaFilters,
      searchTerm,
    };
    organisationSocket.emit(eventName, payload);
  };

  const onConversationsSocketConnect = () => {
    const eventName = 'chat-filters-update';
    const payload = {
      tags: selectedTagFilters,
      status: selectedChatStatus,
      slaStatus: selectedSlaStatus,
      assignedUserId:
        assignedUserId && assignedUserId !== 'null' ? assignedUserId : assignedUserId === 'null' ? null : undefined,
      waba: selectedWabaFilters,
      searchTerm,
    };
    conversationSocket.emit(eventName, payload);
  };

  useEffect(() => {
    document.addEventListener('organizations-socket-connect', onOrganizationSocketConnect);

    return () => {
      document.removeEventListener('organizations-socket-connect', onOrganizationSocketConnect);
    };
  }, [onOrganizationSocketConnect]);

  useEffect(() => {
    document.addEventListener('conversation-socket-connect', onConversationsSocketConnect);

    return () => {
      document.removeEventListener('conversation-socket-connect', onConversationsSocketConnect);
    };
  }, [onConversationsSocketConnect]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const userId = queryParams.get('assignedUserId') ?? '';
    const slaStatus = (queryParams.get('slaStatus') as ChatFilterSlaStatus) ?? 'non-breached';
    if (userId === 'null' && expandedChatFilterTypes.indexOf(FiltersState.UNASSIGNED) === -1) {
      setExpandedChatFilterTypes([FiltersState.UNASSIGNED]);
    } else if (userId === currentUserId && expandedChatFilterTypes.indexOf(FiltersState.MY_CHATS) === -1) {
      setExpandedChatFilterTypes([FiltersState.MY_CHATS]);
    } else if (slaStatus === 'breached' && expandedChatFilterTypes.indexOf(FiltersState.SLA_STATUS) === -1) {
      setExpandedChatFilterTypes([FiltersState.SLA_STATUS]);
    } else if (expandedChatFilterTypes.indexOf(FiltersState.ALL_CHATS) === -1) {
      setExpandedChatFilterTypes([FiltersState.ALL_CHATS]);
    }
  }, [setExpandedChatFilterTypes]);

  useEffect(() => {
    if (expandedChatFilterTypes && !expandedChatFilterTypes.includes(FiltersState.UNASSIGNED)) {
      const queryParams = new URLSearchParams(location.search);
      const isAssigneeFilterUsed = assignedUserId && assignedUserId !== 'null';

      // Only remove assignedUserId if UNASSIGNED filter was previously used and we're not currently using the ASSIGNEE filter
      if (queryParams.get('assignedUserId') === 'null' && !isAssigneeFilterUsed) {
        queryParams.delete('assignedUserId');
        routerNavigate({
          pathname: window.location.pathname,
          search: queryParams.toString(),
        });
      }
    }
  }, [expandedChatFilterTypes, assignedUserId]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);

    if (queryParams.size === 0 && chatStatusFilterCounts?.length && currentUserId) {
      const firstFilter = chatStatusFilterCounts[0];
      const { type, counts } = firstFilter;

      if (type !== FiltersState.ALL_CHATS) {
        setExpandedChatFilterTypes([type as FiltersState]);
        queryParams.set('status', counts[0]?.type ?? '');

        switch (type) {
          case FiltersState.MY_CHATS:
            queryParams.set('assignedUserId', currentUserId);
            break;
          case FiltersState.UNASSIGNED:
            queryParams.set('assignedUserId', 'null');
            break;
          case FiltersState.SLA_STATUS:
            queryParams.set('assignedUserId', '');
            queryParams.set('slaStatus', 'breached');
            break;
          default:
            break;
        }

        const updatedSearch = queryParams.toString();
        if (updatedSearch !== location.search) {
          navigate({ search: updatedSearch }, { replace: true });
        }
      }
    }
  }, [location.search, chatStatusFilterCounts, currentUserId]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const userId = queryParams.get('assignedUserId') ?? '';
    const slaStatus = (queryParams.get('slaStatus') as ChatFilterSlaStatus) ?? 'non-breached';
    const status = (queryParams.get('status') as ChatStatuses) ?? ChatStatuses.UNRESOLVED;
    const conversationViewFromParams = queryParams.get('chatType') === 'WA_GROUP' ? 'groups' : 'chats';

    const tags = deserializeTagsFromQueryParams(queryParams);
    const tagIdsFromParams = deserializeTagIdsFromQueryParams(queryParams);
    const waba = queryParams.has('waba') ? (queryParams.get('waba') as string).split(',').filter(Boolean) : [];
    if (
      prevLeadCustomFieldFeildId.current == leadCustomFieldIdList?.fieldId &&
      userId === assignedUserId &&
      slaStatus === selectedSlaStatus &&
      status === selectedChatStatus &&
      conversationViewFromParams === conversationView &&
      tags.filter(Boolean).sort().join(',') === selectedTagFilters.filter(Boolean).sort().join(',') &&
      waba.filter(Boolean).sort().join(',') === selectedWabaFilters.filter(Boolean).sort().join(',')
    ) {
      return;
    }

    if (leadCustomFieldIdList?.listId && prevLeadCustomFieldFeildId.current !== leadCustomFieldIdList?.fieldId) {
      prevLeadCustomFieldFeildId.current = leadCustomFieldIdList?.fieldId;
    }

    setChatFilters({
      type: ChatListReducerActionTypes.MIXED,
      data: {
        assignedUserId: userId,
        slaStatus,
        status,
        conversationView: conversationViewFromParams,
        tags,
        waba,
        tagIds: tagIdsFromParams,
      },
    });
    sendRealTimeChatFiltersData({
      assignedUserId: userId ? userId : undefined,
      slaStatus,
      status,
      tags,
      waba,
    });
  }, [
    location.search,
    assignedUserId,
    selectedSlaStatus,
    selectedChatStatus,
    conversationView,
    selectedTagFilters,
    selectedWabaFilters,
    leadCustomFieldIdList,
    sendRealTimeChatFiltersData,
    setChatFilters,
  ]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const hideChatFilters = queryParams.get('showChatFilters') === 'false';
    if (isEmbed && hideChatFilters) {
      setShowChatFilters({ open: false });
    }
  }, [location.search]);

  const clearAppliedTags = () => {
    const queryParams = new URLSearchParams(location.search);
    queryParams.set('tags', '');
    queryParams.set('tagIds', '');
    navigate({ search: queryParams.toString() }, { replace: true });
  };

  const clearAppliedWaba = () => {
    const queryParams = new URLSearchParams(location.search);
    queryParams.set('waba', '');
    navigate({ search: queryParams.toString() }, { replace: true });
  };

  // Determine which tags data to use: search results if searching, otherwise initial list
  const conversationTagsRequestDataForDropdown = useMemo(() => {
    const isSearching = conversationTagsSearchTerm && conversationTagsSearchTerm.trim().length > 0;
    const sourceData = isSearching ? conversationTagsSearchRequestData : conversationTagsRequestData;
    return {
      ...sourceData,
      data: tagFilterDropdownOpen
        ? sourceData.data
        : sourceData.data?.sort((a, b) => sortTagsBasedOnSelection(a, b, selectedTagFilters)),
    };
  }, [
    conversationTagsRequestData,
    conversationTagsSearchRequestData,
    conversationTagsSearchTerm,
    selectedTagFilters,
    tagFilterDropdownOpen,
  ]);

  // Determine which WABA data to use: search results if searching, otherwise initial list
  const wabaIntegrationsRequestDataForDropdown = useMemo(() => {
    const isSearching = wabaIntegrationSearchTerm && wabaIntegrationSearchTerm.trim().length > 0;
    return isSearching ? wabaIntegrationsSearchRequestData : wabaIntegrationsRequestData;
  }, [wabaIntegrationsRequestData, wabaIntegrationsSearchRequestData, wabaIntegrationSearchTerm]);

  if (isEmbed && new URLSearchParams(location.search).get('showChatFilters') === 'false') {
    return null;
  }

  const renderFilters = () => {
    if (chatStatusFilterCountsLoading && !haveLoadedFilters) {
      return (
        <Box
          css={css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 200px;
          `}
        >
          <Loader />
        </Box>
      );
    }
    if (previousFilterCountsErrorRef.current && !haveLoadedFilters && (chatStatusFilterCounts?.length ?? 0) === 0) {
      return <ErrorInAPIComp onRetry={() => fetchChatStatusFilterCountsData()} />;
    }

    const renderChatStatusFilter = ({
      label,
      count,
      isActive,
      handleClick,
      index,
      type,
    }: {
      label: string;
      isActive: boolean;
      handleClick: () => void;
      index: number;
      count?: string;
      type: string;
    }) => {
      return (
        <ButtonBase<'div'>
          key={index}
          css={css`
            height: 36px;
            width: 100%;
            display: flex;
            justify-content: space-between;
            align-items: center;
            column-gap: 8px;
            padding: 8px 16px 8px 46px;
            cursor: pointer;
            background-color: ${isActive ? '#EAF3F0' : '#FFFFFF'};
            box-shadow: none;
            outline: none;
            border: none;

            &:hover {
              background-color: ${isActive ? '#EAF3F0' : 'rgba(0, 0, 0, 0.04)'};
            }

            &:hover,
            &:focus,
            &:active {
              box-shadow: none;
              outline: none;
              border: none;
            }
          `}
          disabled={paginatedChatsLoading}
          onClick={handleClick}
          component="div"
        >
          <Box sx={{ display: 'flex', gap: '8px', justifyContent: 'start', alignItems: 'center' }}>
            <QSTypography
              css={css`
                font-size: 14px;
                font-weight: ${isActive ? '600' : '500'};
                color: ${isActive ? '#3F906D' : '#00000099'};
                flex: 0 1 auto;
                white-space: nowrap;
                text-overflow: ellipsis;
                overflow: hidden;
                word-wrap: break-word;
                word-break: break-all;
              `}
            >
              {label}
            </QSTypography>
            {(!chatStatusFilterCountsLoading || filterCountsApiSucceededOnceRef.current) &&
              typeof count === 'string' &&
              count !== '0' && (
                <QSTypography
                  css={css`
                    font-size: 12px;
                    font-weight: 600;
                    line-height: 135%;
                    padding: 2px 6px;
                    border-radius: 5px;
                    color: ${isActive ? '#FFFFFF' : '#7A7A7A'};
                    background-color: ${isActive ? '#3F906D' : '#ECECEC'};
                  `}
                >
                  {count}
                </QSTypography>
              )}
          </Box>
          {type === ChatStatuses.WAITING_FOR_RESPONSE && (
            <QSTooltip title="Let AI scan and show chats that actually need a reply">
              <Box
                sx={{
                  position: 'relative',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '20px',
                  height: '20px',
                  minWidth: '20px',
                  minHeight: '20px',
                  overflow: 'visible',
                  cursor: 'pointer',
                  '& img': {
                    position: 'absolute',
                    objectFit: 'contain',
                  },
                  '& .ai-awaiting-default': {
                    width: '20px',
                    height: '20px',
                    opacity: 1,
                  },
                  '& .ai-awaiting-hover': {
                    width: '20px',
                    height: '20px',
                    transform: 'scale(1.167)',
                    transformOrigin: 'center',
                    opacity: 0,
                    visibility: 'hidden',
                  },
                  '&:hover .ai-awaiting-default': {
                    opacity: 0,
                    visibility: 'hidden',
                  },
                  '&:hover .ai-awaiting-hover': {
                    opacity: 1,
                    visibility: 'visible',
                  },
                  marginTop: '1.5px',
                  marginRight: '2px',
                }}
              >
                <img src={AiAwaitingReplyDisabledImg} alt="" className="ai-awaiting-default" />
                <img src={AiAwaitingReplyImg} alt="" className="ai-awaiting-hover" />
              </Box>
            </QSTooltip>
          )}
        </ButtonBase>
      );
    };

    const renderConversationViewToggle = () => (
      <Box
        css={css`
          width: 100%;
          padding: 12px 8px 0 16px;
          background: #ffffff;
        `}
      >
        <QSTypography
          css={css`
            color: rgba(0, 0, 0, 0.6);
            font-family: Inter;
            font-size: 14px;
            font-style: normal;
            font-weight: 500;
            line-height: 21px;
            margin-bottom: 8px;
            :hover {
              color: #2d2d2d;
            }
          `}
        >
          View
        </QSTypography>
        <Box
          css={css`
            height: 36px;
            border-radius: 4px;
            border: 1px solid #e0e0e9;
            background: white;
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
          `}
        >
          <ButtonBase<'div'>
            component="div"
            onClick={() => {
              const queryParams = new URLSearchParams(location.search);
              queryParams.delete('chatType');
              const newSearch = queryParams.toString();
              routerNavigate(
                {
                  pathname: location.pathname,
                  search: newSearch ? `?${newSearch}` : '',
                },
                { replace: true }
              );
            }}
            css={css`
              border-radius: 4px 0 0 4px;
              display: flex;
              justify-content: center;
              align-items: center;
              column-gap: 6px;
              background: ${conversationView === 'chats' ? '#EAF3F0' : 'transparent'};
              color: ${conversationView === 'chats' ? '#3F906D' : '#666666'};
            `}
          >
            <ChatAllIcon color={conversationView === 'chats' ? '#3F906D' : '#666666'} />
            <QSTypography
              css={css`
                font-size: 14px;
                font-weight: 500;
                color: inherit;
              `}
            >
              All Chats
            </QSTypography>
          </ButtonBase>
          <ButtonBase<'div'>
            component="div"
            onClick={() => {
              setCustomFieldConditions({ type: 'clear' });
              const queryParams = new URLSearchParams(location.search);
              queryParams.set('chatType', 'WA_GROUP');
              queryParams.delete('cfc');
              const newSearch = queryParams.toString();
              routerNavigate(
                {
                  pathname: location.pathname,
                  search: newSearch ? `?${newSearch}` : '',
                },
                { replace: true }
              );
            }}
            css={css`
              border-radius: 0 4px 4px 0;
              display: flex;
              justify-content: center;
              align-items: center;
              column-gap: 6px;
              background: ${conversationView === 'groups' ? '#EAF3F0' : 'transparent'};
              color: ${conversationView === 'groups' ? '#3F906D' : '#666666'};
            `}
          >
            <BroadcastGroupIcon color={conversationView === 'groups' ? '#3F906D' : '#666666'} size={20} />
            <QSTypography
              css={css`
                font-size: 14px;
                font-weight: 500;
                color: inherit;
              `}
            >
              Groups
            </QSTypography>
          </ButtonBase>
        </Box>
      </Box>
    );

    return (
      <>
        {previousFilterCountsErrorRef.current &&
          !filterCountsApiSucceededOnceRef.current &&
          haveLoadedFilters &&
          (chatStatusFilterCounts?.length ?? 0) > 0 && (
            <Box
              css={css`
                padding: 8px 12px;
                border-bottom: 1px solid #e0e0e9;
                background: #fff8f0;
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                row-gap: 6px;
              `}
            >
              {chatStatusFilterCountsRefreshing ? (
                <Box
                  sx={{
                    display: 'flex',
                    width: '100%',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 0.5,
                  }}
                >
                  <CircularProgress size={18} sx={{ color: '#666666' }} />
                </Box>
              ) : (
                <>
                  <QSTypography
                    css={css`
                      font-size: 12px;
                      font-weight: 500;
                      color: #666666;
                    `}
                  >
                    Could not refresh filter counts
                  </QSTypography>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => {
                      bypassCacheRef.current = true;
                      fetchChatStatusFilterCountsData(true);
                    }}
                    sx={{ textTransform: 'none', fontWeight: 500, padding: 0, minWidth: 0 }}
                  >
                    Retry
                  </Button>
                </>
              )}
            </Box>
          )}
        {chatStatusFilterCountsMap.map(
          (
            {
              type,
              label,
              defaultExpanded,
              collapsible,
              hideCollapseIcon,
              disableRenderAccordionSummary,
              hideBorderBottom,
              Icon,
              RightIcon,
              feature,
            },
            index
          ) => {
            return (
              <Fragment key={index}>
                <Accordion
                  css={css(
                    chatFilterAccordionStyles,
                    `
                      border-bottom: ${hideBorderBottom ? 'none' : '1px solid #E0E0E9'};
                      &:last-of-type .MuiCollapse-root .MuiAccordion-region .MuiAccordionDetails-root {
                        padding-bottom: 16px;
                      }
                      .MuiButtonBase-root.MuiAccordionSummary-root {
                        column-gap: 8px;
                        padding: ${
                          disableRenderAccordionSummary ? '0px' : collapsible ? '12px 16px' : '12px 16px 8px 4px'
                        };
                        cursor: ${collapsible ? 'pointer' : 'auto'};
                        height: ${showChatFilters.open ? (collapsible ? '44px' : 'auto') : '44px'};
                        min-height: ${showChatFilters.open ? (collapsible ? '44px' : 'auto') : '44px'};
                        max-height: ${showChatFilters.open ? (collapsible ? '44px' : 'auto') : '44px'};
                        transition: height 280ms ease-in-out, min-height 280ms ease-in-out, max-height 280ms ease-in-out;
                      }
                      .MuiButtonBase-root.MuiAccordionSummary-root:hover:not(.Mui-disabled) {
                        cursor: ${collapsible ? 'pointer' : 'auto'};
                      }
                    `
                  )}
                  expanded={collapsible ? expandedChatFilterTypes.indexOf(type) > -1 : true}
                  defaultExpanded={defaultExpanded}
                  onChange={
                    collapsible
                      ? (event, expanded) => {
                          event.stopPropagation();
                          if (feature === PlanAddonTypes.SlaAddon && !isAddonPresent(feature)) {
                            return setPurchaseBlockerModal(PurchaseBlockerModalType.SLA);
                          }
                          setExpandedChatFilterTypes((prev) => {
                            // return expanded ? (prev.indexOf(type) === -1 ? [...prev].concat(type) : [...prev]) : [...prev].filter((filterType) => filterType !== type);
                            return expanded ? (prev.indexOf(type) === -1 ? [type] : [...prev]) : [];
                          });
                        }
                      : undefined
                  }
                >
                  <AccordionSummary
                    css={css`
                      :hover p {
                        color: #2d2d2d !important;
                      }
                    `}
                    expandIcon={
                      collapsible && (feature ? isAddonPresent(feature) : !hideCollapseIcon) ? (
                        <IconButton<'div'>
                          css={css`
                            padding: 2px;
                          `}
                          disabled={paginatedChatsLoading || chatStatusFilterCountsLoading}
                          component="div"
                        >
                          <ArrowExpandIcon color="#7a7a7a" size={28} />
                        </IconButton>
                      ) : null
                    }
                  >
                    {disableRenderAccordionSummary ? (
                      <Fragment />
                    ) : (
                      <Box
                        css={css`
                          width: 100%;
                          display: flex;
                          justify-content: flex-start;
                          align-items: center;
                          column-gap: 12px;
                        `}
                      >
                        <Box
                          css={css`
                            position: relative;
                          `}
                        >
                          {RightIcon && !isAddonPresent(feature) && (
                            <RightIcon
                              css={css`
                                fill: ${expandedChatFilterTypes.indexOf(type) > -1 || isFilterActiveInAccordion(type)
                                  ? '#3F906D'
                                  : '#00000099'};
                                margin-left: auto;
                                height: 15px;
                                width: 15px;
                                background: rgb(224, 224, 224);
                                padding: 2px;
                                border-radius: 50%;
                                border: 1px solid #cdcdcd;
                                position: absolute;
                                right: -5px;
                                top: -5px;
                              `}
                              fill={
                                expandedChatFilterTypes.indexOf(type) > -1 || isFilterActiveInAccordion(type)
                                  ? '#3F906D'
                                  : '#00000099'
                              }
                            />
                          )}
                          {Icon && (
                            <Icon
                              color={
                                expandedChatFilterTypes.indexOf(type) > -1 || isFilterActiveInAccordion(type)
                                  ? '#3F906D'
                                  : '#666666'
                              }
                            />
                          )}
                        </Box>

                        <QSTypography
                          css={css`
                            font-size: 14px;
                            font-weight: 500;
                            color: ${expandedChatFilterTypes.indexOf(type) > -1 || isFilterActiveInAccordion(type)
                              ? '#2D2D2D'
                              : '#00000099'};
                            word-wrap: break-word;
                            word-break: break-all;
                          `}
                        >
                          {label}
                        </QSTypography>
                      </Box>
                    )}
                  </AccordionSummary>
                  <AccordionDetails>
                    {type === FiltersState.STATUS && (
                      <Box
                        css={css`
                          width: 100%;
                          padding: 0px 16px;
                        `}
                      >
                        <DropdownVirtualList<
                          { id: ChatStatuses; name: string },
                          { hasMore: boolean },
                          ChatStatuses,
                          false
                        >
                          disabled={
                            chatStatusesRequestData?.loading ||
                            chatStatusesRequestData?.refreshing ||
                            (typeof chatStatusesRequestData?.error?.message === 'string' &&
                              chatStatusesRequestData?.error?.message?.length > 0)
                          }
                          popoverValue={
                            (chatStatusesRequestData?.data ?? [])?.find(({ id }) => id === selectedChatStatus)?.name ??
                            'Please select a status'
                          }
                          searchTerm={conversationStatusSearchTerm}
                          setSearchTerm={debouncedSetConversationStatusSearchTerm}
                          placeholder="Please select a status"
                          optionsEmptyMessage="No statuses found"
                          name={['name', 'id']}
                          value={selectedChatStatus}
                          onChange={(status) => {
                            const queryParams = new URLSearchParams(location.search);
                            queryParams.set('status', status);
                            navigate({ search: queryParams.toString() }, { replace: true });
                          }}
                          closeOnSelect={true}
                          multiple={false}
                          requestData={chatStatusesRequestData}
                          containerStyles={css`
                            width: 100%;
                            max-width: 100%;
                            display: flex;
                            flex-direction: column;
                            justify-content: flex-start;
                            align-items: flex-start;
                            row-gap: 8px;

                            @media (min-width: 0px) and (max-width: 599px) {
                              max-width: 100%;
                            }
                          `}
                          placeholderStyles={css`
                            font-size: 14px;
                            color: #2d2d2d;
                            font-weight: 400;
                            word-wrap: break-word;
                            word-break: break-all;
                          `}
                          buttonStyles={css`
                            background-color: transparent;
                          `}
                          emptyDataMessageStyles={css`
                            font-size: 14px;
                            font-weight: 400;
                            word-wrap: break-word;
                            word-break: break-all;
                          `}
                        />
                      </Box>
                    )}
                    {type === FiltersState.ALL_CHATS && (
                      <Box
                        css={css`
                          width: 100%;
                          display: flex;
                          flex-direction: column;
                          justify-content: center;
                          align-items: stretch;
                        `}
                      >
                        {chatStatusFilterCounts
                          ?.find?.(({ type }) => type === 'ALL_CHATS')
                          ?.counts?.map?.(({ type, label }, index) => {
                            const count = getChatStatusFilterCount('ALL_CHATS', type);
                            const isActive =
                              assignedUserId !== 'null' &&
                              assignedUserId !== currentUserId &&
                              expandedChatFilterTypes.indexOf(FiltersState.ALL_CHATS) > -1 &&
                              selectedSlaStatus !== 'breached' &&
                              type === selectedChatStatus;
                            const handleAllChatsFilterClick = () => {
                              const queryParams = new URLSearchParams(location.search);
                              queryParams.set('status', type);
                              if (assignedUserId === currentUserId || assignedUserId === 'null') {
                                queryParams.set('assignedUserId', '');
                              }
                              queryParams.set('slaStatus', 'non-breached');
                              navigate(
                                {
                                  search: queryParams.toString(),
                                },
                                { replace: true }
                              );
                            };

                            return renderChatStatusFilter({
                              count,
                              isActive,
                              label,
                              index,
                              handleClick: handleAllChatsFilterClick,
                              type,
                            });
                          })}
                      </Box>
                    )}
                    {type === FiltersState.MY_CHATS && (
                      <Box
                        css={css`
                          width: 100%;
                          display: flex;
                          flex-direction: column;
                          justify-content: center;
                          align-items: stretch;
                        `}
                      >
                        {chatStatusFilterCounts
                          ?.find?.(({ type }) => type === 'MY_CHATS')
                          ?.counts?.map?.(({ type, label }, index) => {
                            const count = getChatStatusFilterCount('MY_CHATS', type);
                            const isActive =
                              typeof assignedUserId === 'string' &&
                              assignedUserId.length > 0 &&
                              assignedUserId === currentUserId &&
                              expandedChatFilterTypes.indexOf(FiltersState.MY_CHATS) > -1 &&
                              selectedSlaStatus !== 'breached' &&
                              type === selectedChatStatus;
                            const handleMyChatsFilterClick = () => {
                              const queryParams = new URLSearchParams(location.search);
                              queryParams.set('status', type);
                              queryParams.set('assignedUserId', currentUserId as string);
                              queryParams.set('slaStatus', 'non-breached');
                              navigate(
                                {
                                  search: queryParams.toString(),
                                },
                                { replace: true }
                              );
                            };

                            return renderChatStatusFilter({
                              count,
                              isActive,
                              label,
                              index,
                              handleClick: handleMyChatsFilterClick,
                              type,
                            });
                          })}
                      </Box>
                    )}
                    {type === FiltersState.SLA_STATUS && isAddonPresent(feature) && (
                      <Box
                        css={css`
                          width: 100%;
                          display: flex;
                          flex-direction: column;
                          justify-content: center;
                          align-items: stretch;
                        `}
                      >
                        {chatStatusFilterCounts
                          ?.find?.(({ type }) => type === 'SLA_STATUS')
                          ?.counts?.map?.(({ type, label }, index) => {
                            const count = getChatStatusFilterCount('SLA_STATUS', type);
                            const isActive =
                              selectedSlaStatus === 'breached' &&
                              expandedChatFilterTypes.indexOf(FiltersState.SLA_STATUS) > -1 &&
                              type === selectedChatStatus;
                            const handleSlaStatusFilterClick = () => {
                              const queryParams = new URLSearchParams(location.search);
                              queryParams.set('status', type);
                              queryParams.set('slaStatus', 'breached');
                              if (selectedSlaStatus !== 'breached') {
                                queryParams.set('assignedUserId', '');
                              }
                              navigate(
                                {
                                  search: queryParams.toString(),
                                },
                                { replace: true }
                              );
                            };

                            return renderChatStatusFilter({
                              count,
                              isActive,
                              label,
                              index,
                              handleClick: handleSlaStatusFilterClick,
                              type,
                            });
                          })}
                      </Box>
                    )}
                    {type === FiltersState.UNASSIGNED && (
                      <Box
                        css={css`
                          width: 100%;
                          display: flex;
                          flex-direction: column;
                          justify-content: center;
                          align-items: stretch;
                        `}
                      >
                        {chatStatusFilterCounts
                          ?.find?.(({ type }) => type === 'UNASSIGNED')
                          ?.counts?.map?.(({ type, label }, index) => {
                            const count = getChatStatusFilterCount('UNASSIGNED', type);
                            const isActive =
                              assignedUserId === 'null' &&
                              expandedChatFilterTypes.indexOf(FiltersState.UNASSIGNED) > -1 &&
                              selectedSlaStatus !== 'breached' &&
                              type === selectedChatStatus;
                            const handleUnassignedStatusFilterClick = () => {
                              const queryParams = new URLSearchParams(location.search);
                              queryParams.set('status', type);
                              queryParams.set('assignedUserId', 'null');
                              queryParams.set('slaStatus', 'non-breached');
                              navigate(
                                {
                                  search: queryParams.toString(),
                                },
                                { replace: true }
                              );
                            };

                            return renderChatStatusFilter({
                              count,
                              isActive,
                              label,
                              index,
                              handleClick: handleUnassignedStatusFilterClick,
                              type,
                            });
                          })}
                      </Box>
                    )}
                    {type === FiltersState.ASSIGNEE && (
                      <Box
                        css={css`
                          width: 100%;
                          padding: 0px 16px;
                        `}
                      >
                        <DropdownVirtualList<TeamMember, { hasMore: boolean }, string, false>
                          disabled={
                            conversationAssigneesRequestData?.loading ||
                            conversationAssigneesRequestData?.refreshing ||
                            (typeof conversationAssigneesRequestData?.error?.message === 'string' &&
                              conversationAssigneesRequestData?.error?.message?.length > 0)
                          }
                          popoverValue={
                            assignedUserId && assignedUserId !== 'null'
                              ? (conversationAssigneesRequestData?.data ?? [])?.find(({ id }) => id === assignedUserId)
                                  ?.name || 'Filter by member'
                              : 'Filter by member'
                          }
                          searchTerm={conversationAssigneeSearchTerm}
                          setSearchTerm={debouncedSetConversationAssigneeSearchTerm}
                          placeholder="Search by member"
                          optionsEmptyMessage="No members found"
                          name={['name', 'id']}
                          value={assignedUserId && assignedUserId !== 'null' ? assignedUserId : ''}
                          onChange={(newAssignedUserId) => {
                            const queryParams = new URLSearchParams(location.search);
                            if (newAssignedUserId) {
                              queryParams.set('assignedUserId', newAssignedUserId);
                            } else {
                              queryParams.delete('assignedUserId');
                            }
                            navigate({ search: queryParams.toString() }, { replace: true });
                          }}
                          customHandleChange={handleTeamMemberChange}
                          open={assigneeFilterDropdownOpen}
                          onDropdownOpen={() => setAssigneeFilterDropdownOpen(true)}
                          onDropdownClose={() => setAssigneeFilterDropdownOpen(false)}
                          closeOnSelect={false}
                          multiple={false}
                          requestData={conversationAssigneesRequestData}
                          containerStyles={css`
                            width: 100%;
                            max-width: 100%;
                            display: flex;
                            flex-direction: column;
                            justify-content: flex-start;
                            align-items: flex-start;
                            row-gap: 8px;

                            @media (min-width: 0px) and (max-width: 599px) {
                              max-width: 100%;
                            }
                          `}
                          placeholderStyles={css`
                            font-size: 14px;
                            color: #2d2d2d;
                            font-weight: 400;
                            word-wrap: break-word;
                            word-break: break-all;
                          `}
                          buttonStyles={css`
                            background-color: transparent;
                          `}
                          emptyDataMessageStyles={css`
                            font-size: 14px;
                            font-weight: 400;
                            word-wrap: break-word;
                            word-break: break-all;
                          `}
                        />
                      </Box>
                    )}
                    {type === FiltersState.TAGS && (
                      <Box
                        css={css`
                          width: 100%;
                          padding: 0px 16px;
                        `}
                      >
                        <DropdownVirtualList<ConversationTag, GetConversationTagsPaginationData, string, true>
                          disabled={
                            conversationTagsRequestDataForDropdown?.loading ||
                            conversationTagsRequestDataForDropdown?.refreshing ||
                            (typeof conversationTagsRequestDataForDropdown?.error?.message === 'string' &&
                              conversationTagsRequestDataForDropdown?.error?.message?.length > 0)
                          }
                          popoverValue={
                            (selectedTagFilters ?? []).length > 1
                              ? `${selectedTagFilters?.length} tags`
                              : selectedTagFilters?.[0] ?? 'Filter by tags'
                          }
                          showCloseIcon
                          onCloseIconClick={clearAppliedTags}
                          searchTerm={conversationTagsSearchTerm}
                          setSearchTerm={setConversationTagsSearchTerm}
                          placeholder="Search by tags"
                          optionsEmptyMessage="No tags found"
                          name={['name', 'id']}
                          value={tagIds ?? []}
                          onChange={(tagIds) => {
                            const queryParams = new URLSearchParams(location.search);
                            // Use both search results and original data to map IDs to names
                            // This ensures we can find all selected tags even if they're not in search results
                            const allTagsData = [
                              ...(conversationTagsRequestDataForDropdown?.data ?? []),
                              ...(conversationTagsRequestData?.data ?? []),
                            ];
                            // Remove duplicates by ID
                            const uniqueTagsMap = new Map<string, ConversationTag>();
                            allTagsData.forEach((tag) => {
                              if (!uniqueTagsMap.has(tag.id)) {
                                uniqueTagsMap.set(tag.id, tag);
                              }
                            });
                            const serializedTags = JSON.stringify(
                              Array.from(uniqueTagsMap.values())
                                .filter(({ id }) => tagIds.includes(id))
                                .map(({ name }) => name)
                            );
                            const serializedTagIds = JSON.stringify(tagIds ?? []);
                            queryParams.set('tags', serializedTags);
                            queryParams.set('tagIds', serializedTagIds);
                            navigate({ search: queryParams.toString() }, { replace: true });
                          }}
                          closeOnSelect={false}
                          multiple={true}
                          requestData={conversationTagsRequestDataForDropdown}
                          onDropdownClose={() => setTagFilterDropdownOpen(false)}
                          onDropdownOpen={() => setTagFilterDropdownOpen(true)}
                          fetchMore={
                            conversationTagsSearchTerm && conversationTagsSearchTerm.trim().length > 0
                              ? conversationTagsSearchFetchMore
                              : conversationTagsFetchMore
                          }
                          containerStyles={css`
                            width: 100%;
                            max-width: 100%;
                            display: flex;
                            flex-direction: column;
                            justify-content: flex-start;
                            align-items: flex-start;
                            row-gap: 8px;

                            @media (min-width: 0px) and (max-width: 599px) {
                              max-width: 100%;
                            }
                          `}
                          placeholderStyles={css`
                            font-size: 14px;
                            color: #2d2d2d;
                            font-weight: 400;
                            word-wrap: break-word;
                            word-break: break-all;
                          `}
                          buttonStyles={css`
                            background-color: transparent;
                          `}
                          emptyDataMessageStyles={css`
                            font-size: 14px;
                            font-weight: 400;
                            word-wrap: break-word;
                            word-break: break-all;
                          `}
                        />
                      </Box>
                    )}
                    {type === FiltersState.WABA && (
                      <Box
                        css={css`
                          width: 100%;
                          padding: 0px 16px;
                        `}
                      >
                        <DropdownVirtualList<WabaIntegration, WabaIntegrationPaginationParams, string, true>
                          onDropdownOpen={() => refreshWabaIntegrations()}
                          paperWidth="300px"
                          disabled={
                            wabaIntegrationsRequestDataForDropdown?.loading ||
                            wabaIntegrationsRequestDataForDropdown?.refreshing ||
                            (typeof wabaIntegrationsRequestDataForDropdown?.error?.message === 'string' &&
                              wabaIntegrationsRequestDataForDropdown?.error?.message?.length > 0)
                          }
                          popoverValue={
                            (selectedWabaFilters ?? []).length > 1
                              ? `${selectedWabaFilters?.length} Channels`
                              : wabaIntegrationsRequestDataForDropdown?.data?.find?.(
                                  ({ id }) => id === selectedWabaFilters?.[0]
                                )?.displayName ??
                                wabaIntegrationsRequestDataForDropdown?.data?.find?.(
                                  ({ id }) => id === selectedWabaFilters?.[0]
                                )?.name ??
                                wabaIntegrationsRequestDataForDropdown?.data?.find?.(
                                  ({ id }) => id === selectedWabaFilters?.[0]
                                )?.phoneNumber ??
                                'Filter by Channels'
                          }
                          showCloseIcon
                          onCloseIconClick={clearAppliedWaba}
                          searchTerm={wabaIntegrationSearchTerm}
                          setSearchTerm={setWabaIntegrationSearchTerm}
                          placeholder="Search by Channels"
                          optionsEmptyMessage="No Channels found"
                          name={['showWabaName', 'displayName', 'name', 'phoneNumber', 'id']}
                          value={selectedWabaFilters}
                          onChange={(wabaId) => {
                            const queryParams = new URLSearchParams(location.search);
                            queryParams.set('waba', wabaId.join(','));
                            navigate({ search: queryParams.toString() }, { replace: true });
                          }}
                          closeOnSelect={false}
                          multiple={true}
                          fetchMore={
                            wabaIntegrationSearchTerm && wabaIntegrationSearchTerm.trim().length > 0
                              ? wabaIntegrationsSearchFetchMore
                              : wabaIntegrationsFetchMore
                          }
                          requestData={wabaIntegrationsRequestDataForDropdown}
                          containerStyles={css`
                            width: 100%;
                            max-width: 100%;
                            display: flex;
                            flex-direction: column;
                            justify-content: flex-start;
                            align-items: flex-start;
                            row-gap: 8px;

                            @media (min-width: 0px) and (max-width: 599px) {
                              max-width: 100%;
                            }
                          `}
                          placeholderStyles={css`
                            font-size: 14px;
                            color: #2d2d2d;
                            font-weight: 400;
                            word-wrap: break-word;
                            word-break: break-all;
                          `}
                          buttonStyles={css`
                            background-color: transparent;
                          `}
                          emptyDataMessageStyles={css`
                            font-size: 14px;
                            font-weight: 400;
                            word-wrap: break-word;
                            word-break: break-all;
                          `}
                          renderItemPrefix={(waba) => (
                            <WabaIntegrationIcon
                              size={28}
                              wabaIntegrationLogo={waba.logo ?? ''}
                              wabaIntegrationName={waba.displayName ?? waba.name ?? waba.phoneNumber ?? ''}
                              channelType={waba.type}
                            />
                          )}
                        />
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
                {type === FiltersState.UNASSIGNED && renderConversationViewToggle()}
              </Fragment>
            );
          }
        )}
        <CustomFieldFilterSidebarEntry />
      </>
    );
  };

  return (
    <Box
      css={css`
        display: flex;
        flex-direction: column;
        justify-content: stretch;
        align-items: flex-start;
        height: 100%;
        width: ${showChatFilters.open ? '240px' : '55px'};
        min-width: ${showChatFilters.open ? '240px' : '55px'};
        max-width: ${showChatFilters.open ? '240px' : '55px'};
        border-right: 1px solid #e0e0e9;
        background-color: ${showChatFilters.open ? '#FFFFFF' : '#FBFBFC'};
        transition: width 280ms ease-in-out, min-width 280ms ease-in-out, max-width 280ms ease-in-out;
        overflow: hidden;
      `}
    >
      {showChatFilters.open ? (
        <Fragment>
          <Box
            css={css`
              display: flex;
              justify-content: space-between;
              align-items: center;
              height: 55px;
              min-height: 55px;
              max-height: 55px;
              width: 240px;
              min-width: 240px;
              max-width: 240px;
              padding: 0px 12px;
              border-bottom: 1px solid #e0e0e9;
            `}
          >
            <QSTypography
              css={css`
                font-size: 16px;
                font-weight: 600;
                flex: 1 1 auto;
                word-wrap: break-word;
                word-break: break-all;
              `}
            >
              Inbox
            </QSTypography>
            <Box>
              {hideDTDetails ? null : (
                <QSTooltip title="Learn about chat filters" placement="top">
                  <Link href="https://learn.doubletick.io/chat-management/chat-inbox" target="_blank" rel="noreferrer">
                    <IconButton<'div'>
                      component="div"
                      onMouseEnter={() => setHelpIconHovered(true)}
                      onMouseLeave={() => setHelpIconHovered(false)}
                    >
                      <HelpIcon color="#7A7A7A" />
                    </IconButton>
                  </Link>
                </QSTooltip>
              )}
              {searchTerm && searchTerm.length > 0 && (
                <RefreshChatCountIcon
                  disabled={paginatedChatsLoading || chatStatusFilterCountsLoading}
                  onRefresh={refreshFilterCounts}
                  resolution={realTimeChatCountStatus?.liveChatCountsResolution ?? null}
                />
              )}

              <QSTooltip title="Close filters" placement="top">
                <IconButton<'div'>
                  disabled={paginatedChatsLoading || chatStatusFilterCountsLoading}
                  onClick={() => setShowChatFilters({ open: false })}
                  component="div"
                >
                  <MenuToggleIcon />
                </IconButton>
              </QSTooltip>
            </Box>
          </Box>
          <Box
            css={css`
              display: flex;
              flex-direction: column;
              height: calc(100% - 55px);
              width: 100%;

              overflow: hidden;
              scrollbar-gutter: stable;

              &:hover {
                overflow-y: auto;
              }
            `}
          >
            {renderFilters()}
          </Box>
        </Fragment>
      ) : (
        <Box
          css={css`
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            align-items: flex-start;
            height: 100%;
            width: 100%;
            overflow: auto;
          `}
        >
          <Box
            css={css`
              height: 55px;
              min-height: 55px;
              max-height: 55px;
              width: 100%;
              min-width: 100%;
              max-width: 100%;
              display: flex;
              justify-content: center;
              align-items: center;
              border-bottom: 1px solid #e0e0e9;
              padding: 0px calc(calc(55px - 32px - 1px) / 2);
            `}
          >
            <Tooltip title="Open filters" placement="right">
              <IconButton<'div'>
                disabled={paginatedChatsLoading || chatStatusFilterCountsLoading}
                onClick={() => setShowChatFilters({ open: true })}
                component="div"
                sx={{
                  transform: 'scale(-1, -1)',
                }}
              >
                <MenuToggleIcon />
              </IconButton>
            </Tooltip>
          </Box>
          {(chatStatusFilterCounts ?? [])?.findIndex?.(
            (chatStatusFilterCount) => chatStatusFilterCount.type === FiltersState.ALL_CHATS
          ) > -1 && (
            <Box
              css={css`
                height: 44px;
                min-height: 44px;
                max-height: 44px;
                width: 100%;
                min-width: 100%;
                max-width: 100%;
                display: flex;
                justify-content: center;
                align-items: center;
                border-bottom: 1px solid #e0e0e9;
                padding: 0px calc(calc(55px - 32px - 1px) / 2);
              `}
            >
              <Tooltip title="All chats" placement="right">
                <IconButton<'div'>
                  disabled={paginatedChatsLoading || chatStatusFilterCountsLoading}
                  onClick={() => {
                    setExpandedChatFilterTypes((prev) => {
                      // return prev.indexOf(FiltersState.ALL_CHATS) === -1 ? [...prev].concat(FiltersState.ALL_CHATS) : [...prev];
                      return [FiltersState.ALL_CHATS];
                    });
                    setShowChatFilters({ open: true });
                  }}
                  component="div"
                >
                  <ChatAllIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          {(chatStatusFilterCounts ?? [])?.findIndex?.(
            (chatStatusFilterCount) => chatStatusFilterCount.type === FiltersState.MY_CHATS
          ) > -1 && (
            <Box
              css={css`
                height: 44px;
                min-height: 44px;
                max-height: 44px;
                width: 100%;
                min-width: 100%;
                max-width: 100%;
                display: flex;
                justify-content: center;
                align-items: center;
                border-bottom: 1px solid #e0e0e9;
                padding: 0px calc(calc(55px - 32px - 1px) / 2);
              `}
            >
              <Tooltip title="My chats" placement="right">
                <IconButton<'div'>
                  disabled={paginatedChatsLoading || chatStatusFilterCountsLoading}
                  onClick={() => {
                    setExpandedChatFilterTypes((prev) => {
                      // return prev.indexOf(FiltersState.MY_CHATS) === -1 ? [...prev].concat(FiltersState.MY_CHATS) : [...prev];
                      return [FiltersState.MY_CHATS];
                    });
                    setShowChatFilters({ open: true });
                  }}
                  component="div"
                >
                  <MyChatsIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          {(chatStatusFilterCounts ?? [])?.findIndex?.(
            (chatStatusFilterCount) => chatStatusFilterCount.type === FiltersState.SLA_STATUS
          ) > -1 && (
            <Box
              css={css`
                height: 44px;
                min-height: 44px;
                max-height: 44px;
                width: 100%;
                min-width: 100%;
                max-width: 100%;
                display: flex;
                justify-content: center;
                align-items: center;
                border-bottom: 1px solid #e0e0e9;
                padding: 0px calc(calc(55px - 32px - 1px) / 2);
              `}
            >
              <Tooltip title="SLA breached" placement="right">
                <IconButton<'div'>
                  disabled={paginatedChatsLoading || chatStatusFilterCountsLoading}
                  onClick={() => {
                    setExpandedChatFilterTypes((prev) => {
                      // return prev.indexOf(FiltersState.SLA_STATUS) === -1 ? [...prev].concat(FiltersState.SLA_STATUS) : [...prev];
                      return [FiltersState.SLA_STATUS];
                    });
                    setShowChatFilters({ open: true });
                  }}
                  component="div"
                >
                  <SLABreachedIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          {(chatStatusFilterCounts ?? [])?.findIndex?.(
            (chatStatusFilterCount) => chatStatusFilterCount.type === FiltersState.UNASSIGNED
          ) > -1 && (
            <Box
              css={css`
                height: 44px;
                min-height: 44px;
                max-height: 44px;
                width: 100%;
                min-width: 100%;
                max-width: 100%;
                display: flex;
                justify-content: center;
                align-items: center;
                border-bottom: 1px solid #e0e0e9;
                padding: 0px calc(calc(55px - 32px - 1px) / 2);
              `}
            >
              <Tooltip title="Unassigned" placement="right">
                <IconButton<'div'>
                  disabled={paginatedChatsLoading || chatStatusFilterCountsLoading}
                  onClick={() => {
                    setExpandedChatFilterTypes((prev) => {
                      // return prev.indexOf(FiltersState.UNASSIGNED) === -1 ? [...prev].concat(FiltersState.UNASSIGNED) : [...prev];
                      return [FiltersState.UNASSIGNED];
                    });
                    setShowChatFilters({ open: true });
                    const queryParams = new URLSearchParams(location.search);
                    queryParams.set('assignedUserId', 'null');
                    queryParams.set('slaStatus', 'non-breached');
                    navigate({ search: queryParams.toString() }, { replace: true });
                  }}
                  component="div"
                >
                  <UnassignedChatsIcon
                    css={css`
                      fill: ${assignedUserId === 'null' && selectedSlaStatus === 'non-breached'
                        ? '#3F906D'
                        : '#00000099'};
                      height: 18px;
                      min-height: 18px;
                      max-height: 18px;
                      width: 18px;
                      min-width: 18px;
                      max-width: 18px;
                    `}
                    fill={assignedUserId === 'null' && selectedSlaStatus === 'non-breached' ? '#3F906D' : '#00000099'}
                  />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      )}
      <CustomFieldFilterPopovers />
    </Box>
  );
};

export default ChatFilters;
